import {
  Badge,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  capitalize,
  cn,
  formatAmount,
  invoiceParkStatus,
  toApiSort,
} from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useGetDocumentTypeDropdown,
  useGetMJEParkedList,
  useGetMJEPostedList,
} from "@/services";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { formValues } from "./validations";
import {
  DOCUMENT_TYPE,
  parkDocumentSearch,
  postDocumentSearch,
  SEARCH_CRITERIA,
  SEARCH_TYPE,
  searchType,
} from "./consts";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";

const useInvoiceListing = () => {
  const { status } = useParams();
  const isPosted = status == "posted";
  const queryClient = useQueryClient();
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const form = useForm<formValues>({
    defaultValues: {
      [DOCUMENT_TYPE]: "",
      [SEARCH_TYPE]: "",
      [SEARCH_CRITERIA]: "",
    },
  });

  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    queryClient.removeQueries({queryKey: ["mje-parked-list"]});
    queryClient.removeQueries({queryKey: ["mje-posted-list"]});
  }, []);
  
  const {data: documentTypeList, isLoading: isListLoading} = useGetDocumentTypeDropdown({
    communityId: communityData?.communityId,
    queryParams: {
      enabled: !!communityData?.communityId
    }
  })

  const documentType = useWatch({control: form.control, name: DOCUMENT_TYPE});

  const {
    data: parkedDocumentList,
    isLoading: isParkedDocLoading,
    refetch: refetchParkedDoc,
    isRefetching: isParkedDocRefetching,
  } = useGetMJEParkedList({
    communityId: communityData?.communityId,
    data: {
      filters: [
        {
          field: "communityId",
          value: communityData?.communityId,
          operator: "EQUAL",
        },
        {
          field: "documentStatus",
          value: 'POSTED',
          operator: "NOT_EQUAL",
        },
        {
          field: "documentTypeId",
          value: documentType,
          operator: "EQUAL",
        },
        {
          field: "sourceSystem",
          value: "MANUAL_JE",
          operator: "EQUAL",
        },
        ...(form.watch()[SEARCH_TYPE]
          ? [
              {
                field: form.watch()[SEARCH_TYPE],
                value: form.watch()[SEARCH_CRITERIA],
                operator:
                  (
                    [searchType, parkDocumentSearch]?.find(
                      (data: any) =>
                        data.value == (form.watch()[SEARCH_TYPE] as string)
                    ) as any
                  )?.operator || "EQUAL",
              },
            ]
          : []),
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    },
    queryParams: {
      enabled: false,
    },
  });

  const {
    data: postedDocumentList,
    isLoading: isPostedDocLoading,
    refetch: refetchPostedDoc,
    isRefetching: isPostedDocRefetching,
  } = useGetMJEPostedList({
    communityId: communityData?.communityId,
    data: {
      filters: [
        {
          field: "communityId",
          value: communityData?.communityId,
          operator: "EQUAL",
        },
        {
          field: "documentStatus",
          value: 'POSTED',
          operator: "EQUAL",
        },
        {
          field: "documentTypeId",
          value: documentType,
          operator: "EQUAL",
        },
        ...(form.watch()[SEARCH_TYPE]
          ? [
              {
                field: form.watch()[SEARCH_TYPE],
                value: form.watch()[SEARCH_CRITERIA],
                operator:
                  (
                    [searchType, postDocumentSearch]?.find(
                      (data: any) =>
                        data.value == (form.watch()[SEARCH_TYPE] as string)
                    ) as any
                  )?.operator || "EQUAL",
              },
            ]
          : []),
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    },
    queryParams: {
      enabled: false,
    },
  });

  const refetchDoc = isPosted ? refetchPostedDoc : refetchParkedDoc;
  const isDocLoading = isPosted ? isPostedDocLoading : isParkedDocLoading;
  const documentList = isPosted ? postedDocumentList?.data : parkedDocumentList?.data;
  const isDocRefetching = isPosted
    ? isPostedDocRefetching
    : isParkedDocRefetching;

  useEffect(() => {
    if (documentType) {
      refetchDoc();
    }
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0 && documentType) {
      refetchDoc();
    }
  }, [globalFilter]);

  const onSubmit = () => {
    refetchDoc();
  };

  const onClear = () => {
    form.reset();
    queryClient.removeQueries({queryKey: ["mje-parked-list"]});
    queryClient.removeQueries({queryKey: ["mje-posted-list"]});
  };

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.admin}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityData?.name || ""}`,
      },
      {
        link: "",
        name: (
          <span className="text-primary">
            {capitalize(status || "")} JE Manual
          </span>
        ),
      },
    ],
    []
  );

  const postedColumns = [
    {
      accessorKey: "documentNumber",
      header: (
        <div>
          Posted Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row.documentNumber || ""),
    }
  ];

  const parkedColumns = [
    {
      accessorKey: "parkDocumentHeaderId",
      header: (
        <div>
          Parked Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row.parkDocumentHeaderId || ""),
    },
  ];

  const isOB = useMemo(() => {
    return documentTypeList?.find((data: any) => data.documentTypeId == documentType)?.documentType == "OB";
  }, [documentType]);

  // Define Table Columns
  const columns = [
    ...(isPosted ? postedColumns : parkedColumns),
    {
      accessorKey: "createdAt",
      header: <div>Created On</div>,
      accessorFn: (row: any) => row?.createdAt ? moment.utc(row?.createdAt).local().format("MM/DD/YYYY HH:mm:ss") : '',
    },
    {
      accessorKey: "invoiceAmount",
      meta: { align: "right" },
      header: <div>{isOB ? 'Billed Amount' : 'Invoice Amt.'}</div>,
      accessorFn: (row: any) => formatAmount(row?.invoiceAmount || 0),
    },
    {
      accessorKey: "approvedAmount",
      meta: { align: "right" },
      header: <div>Approved Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.approvedAmount || 0),
    },
    {
      accessorKey: "documentStatus",
      header: <div>Status</div>,
      cell: (row: any) => (
        <Badge
          variant="outline"
          className={cn(
            invoiceParkStatus[row.row.original.documentStatus?.toLowerCase()]?.color,
            "rounded-full px-4 py-2"
          )}
        >
          {invoiceParkStatus[row?.row?.original?.documentStatus?.toLowerCase()]?.label}
        </Badge>
      ),
    }
  ];

  const onRowClick = (row: any) => {
    if (isPosted) {
      navigate(`${row.original.postDocumentHeaderId}/view`);
    } else {
      navigate(
        `${row.original.parkDocumentHeaderId}/view`
      );
    }
  };

  return {
    breadcrumbData,
    columns,
    globalFilter,
    setGlobalFilter,
    isMobile,
    onRowClick,
    isDocLoading,
    documentList,
    isDocRefetching,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    form,
    onSubmit,
    onClear,
    isPosted,
    currentFinanceYear,
    isListLoading,
    documentTypeList
  };
};

export default useInvoiceListing;
